import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";

const PAGE_HINTS = {
  "/": {
    auto: "Привіт! Я Беррі, гід по SILÉA. Хочеш покажу нашу нову колекцію Riviera?",
    hint: "Привіт! Я Беррі. Чим можу допомогти?",
    quick: [
      { label: "Показати купальники", to: "/products" },
      { label: "Створити власний дизайн", to: "/customizer" },
      { label: "Про бренд", to: "/about" },
    ],
  },
  "/products": {
    auto: "Не поспішай — кожна модель має свій настрій. Якщо щось сподобається, я підкажу як обрати розмір 🐾",
    hint: "Допомогти обрати модель?",
    quick: [
      { label: "Як обрати розмір?", action: "size" },
      { label: "Створити свій дизайн", to: "/customizer" },
      { label: "Доставка", action: "delivery" },
    ],
  },
  "/about": {
    auto: "Раді, що ти заглянула. Тут трохи про філософію SILÉA ✨",
    hint: "Що тебе цікавить?",
    quick: [
      { label: "Перейти до колекції", to: "/products" },
      { label: "Створити власний дизайн", to: "/customizer" },
    ],
  },
  "/checkout": {
    auto: "Майже готово! Я перевірю, щоб усе було добре. Якщо щось — клікай по мені.",
    hint: "Питання щодо замовлення?",
    quick: [
      { label: "Як відбувається оплата?", action: "payment" },
      { label: "Доставка", action: "delivery" },
    ],
  },
  "/customizer": {
    auto: "Це моя улюблена сторінка! Створімо купальник твоєї мрії разом 🍋",
    hint: "Як я можу допомогти з дизайном?",
    quick: [
      { label: "Скільки коштує кастомізація?", action: "custom-price" },
      { label: "Скільки часу виготовляють?", action: "custom-time" },
    ],
  },
  "/thank-you": {
    auto: "Дякую за замовлення! Менеджер зв'яжеться з тобою найближчим часом 💛",
    hint: "Що далі?",
    quick: [{ label: "Повернутись до колекції", to: "/products" }],
  },
};

const ANSWERS = {
  size: "Наші розміри: XS, S, M — у наявності, L — під замовлення (7–10 робочих днів). Для точного підбору вимірюй груди та стегна.",
  delivery: "Доставляємо Новою Поштою по Україні. Стандартні замовлення — 1–3 дні після підтвердження. Кастомні — 7–10 робочих днів.",
  payment: "Після підтвердження замовлення менеджер надішле реквізити для оплати. Готуємо інтеграцію LiqPay і Mono Acquiring.",
  "custom-price": "350 ₴ за елемент, але дизайн з 5+ елементами — фіксовано 1 500 ₴.",
  "custom-time": "Кастомні купальники готуємо вручну за 7–10 робочих днів.",
};

export const BerryAssistant = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [autoBubble, setAutoBubble] = useState(false);
  const dismissedRef = useRef(false);

  const key = Object.keys(PAGE_HINTS).find((k) => k !== "/" && pathname.startsWith(k)) || "/";
  const data = PAGE_HINTS[key];

  // Auto-popup on page change
  useEffect(() => {
    setAnswer(null);
    if (open || dismissedRef.current) return;
    const showT = setTimeout(() => setAutoBubble(true), 800);
    const hideT = setTimeout(() => setAutoBubble(false), 7500);
    return () => { clearTimeout(showT); clearTimeout(hideT); };
  }, [pathname, open]);

  const onAction = (q) => {
    if (q.to) { setOpen(false); navigate(q.to); }
    else if (q.action) setAnswer(ANSWERS[q.action]);
  };

  const dismissBubble = (e) => {
    e?.stopPropagation();
    setAutoBubble(false);
    dismissedRef.current = true;
  };

  return (
    <>
      {/* Auto speech bubble */}
      {autoBubble && !open && (
        <div
          data-testid="berry-auto-bubble"
          onClick={() => { setOpen(true); setAutoBubble(false); }}
          className="fixed bottom-28 right-6 z-50 max-w-[280px] bg-white border border-stone-200 px-4 py-3 cursor-pointer rounded-sm"
          style={{ boxShadow: "0 14px 36px rgba(10,25,47,0.18)", animation: "fadeUp 500ms ease forwards" }}
        >
          <button onClick={dismissBubble} className="absolute -top-2 -right-2 bg-silea-navy text-white rounded-full w-5 h-5 flex items-center justify-center" data-testid="bubble-close" aria-label="закрити">
            <X size={11} strokeWidth={2.5} />
          </button>
          <p className="text-sm text-silea-navy leading-snug">{data.auto}</p>
          <span className="absolute -bottom-2 right-8 w-3 h-3 bg-white border-r border-b border-stone-200 transform rotate-45" />
        </div>
      )}

      {/* Floating button */}
      <button
        data-testid="berry-fab"
        onClick={() => { setOpen((v) => !v); setAutoBubble(false); }}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full overflow-hidden border-2 border-silea-yellow bg-silea-cream hover:scale-105 transition-transform"
        aria-label="Помічник Беррі"
        style={{ boxShadow: "0 8px 30px rgba(10,25,47,0.25)" }}
      >
        <img src="/generated/berry.webp" alt="Беррі" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ objectPosition: "center 22%", transform: "scale(1.1)" }} />
      </button>

      {/* Dialog */}
      {open && (
        <div
          data-testid="berry-dialog"
          className="fixed bottom-28 right-6 z-50 w-[320px] max-w-[calc(100vw-2rem)] bg-white border border-stone-200 rounded-sm overflow-hidden"
          style={{ boxShadow: "0 20px 60px rgba(10,25,47,0.25)" }}
        >
          <div className="bg-silea-navy text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden border border-silea-yellow bg-silea-cream">
                <img src="/generated/berry.webp" alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ objectPosition: "center 22%", transform: "scale(1.1)" }} />
              </div>
              <div>
                <p className="font-serif-display text-base leading-tight">Беррі</p>
                <p className="eyebrow text-white/60 text-[10px]">помічник SILÉA</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="berry-close" className="text-white/70 hover:text-white"><X size={18} /></button>
          </div>
          <div className="p-4 bg-silea-cream">
            <p className="text-sm text-silea-navy leading-relaxed">{answer || data.hint}</p>
            {answer && (
              <button onClick={() => setAnswer(null)} className="mt-3 eyebrow text-silea-navy/60 hover:text-silea-navy" data-testid="berry-back">← назад</button>
            )}
          </div>
          {!answer && (
            <div className="px-4 pb-4 bg-silea-cream space-y-2">
              {data.quick.map((q, i) => (
                <button
                  key={i}
                  onClick={() => onAction(q)}
                  data-testid={`berry-quick-${i}`}
                  className="w-full text-left text-sm px-3 py-2 border border-stone-300 hover:border-silea-navy hover:bg-white transition-colors text-silea-navy"
                >
                  {q.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};
